import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAppSelector } from './hooks/useAppDispatch';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/Login';
import { OverviewPage } from './pages/Overview';
import { TokensPage } from './pages/Tokens';
import { AttemptsPage } from './pages/Attempts';
import { AppsStatusPage } from './pages/AppsStatus';
import { BillingPage } from './pages/Billing';
import { AuthLoginPage } from './pages/AuthLogin';
import { AuthSignupPage } from './pages/AuthSignup';
import { WorkspacePickerPage } from './pages/WorkspacePicker';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { VerifyEmailPage } from './pages/VerifyEmail';
import { AcceptInvitePage } from './pages/AcceptInvite';
import { StartEntryPage } from './pages/StartEntry';
import { WorkspaceHomePage } from './pages/WorkspaceHome';
import {
  BusinessSetupPage,
  PersonalSetupPage,
  OutcomeSelectionPage,
  PricingSummaryPage,
  ReviewSuggestionsPage,
  InviteTeammatesPage,
  CompletePage,
  NewWorkspacePage,
  OnboardingCompatRedirect,
} from './pages/onboarding';
import { WorkspaceSettingsPage } from './pages/settings/WorkspaceSettings';

function ProtectedRoutes() {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<OverviewPage />} />
        <Route path="/tokens" element={<TokensPage />} />
        <Route path="/attempts" element={<AttemptsPage />} />
        <Route path="/apps" element={<AppsStatusPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export function App() {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  return (
    <BrowserRouter>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#333',
            color: '#fff',
            borderRadius: '8px',
          },
          success: {
            iconTheme: {
              primary: '#25BD71',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <Routes>
        {/* OIDC Auth Flow Routes (public) */}
        <Route path="/auth/login" element={<AuthLoginPage />} />
        <Route path="/auth/signup" element={<AuthSignupPage />} />
        <Route path="/auth/workspace" element={<WorkspacePickerPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />

        {/* Entry point routes (business / personal) */}
        <Route path="/start/:mode" element={<StartEntryPage />} />

        {/* Post-onboarding workspace landing (session-authed page) */}
        <Route path="/home" element={<WorkspaceHomePage />} />

        {/* Onboarding entry / workspace chooser */}
        <Route path="/onboarding/new" element={<NewWorkspacePage />} />

        {/* R7: mode-specific onboarding route trees */}
        <Route path="/onboarding/business/setup" element={<BusinessSetupPage />} />
        <Route path="/onboarding/business/outcomes" element={<OutcomeSelectionPage />} />
        <Route path="/onboarding/business/pricing" element={<PricingSummaryPage />} />
        <Route path="/onboarding/business/review" element={<ReviewSuggestionsPage />} />
        <Route path="/onboarding/business/invites" element={<InviteTeammatesPage />} />
        <Route path="/onboarding/business/complete" element={<CompletePage />} />
        <Route path="/onboarding/personal/setup" element={<PersonalSetupPage />} />
        <Route path="/onboarding/personal/outcomes" element={<OutcomeSelectionPage />} />
        <Route path="/onboarding/personal/pricing" element={<PricingSummaryPage />} />
        <Route path="/onboarding/personal/complete" element={<CompletePage />} />

        {/* R7: legacy flat-route compatibility — redirect to the
            mode-specific route, preserving any ?continue= param. */}
        <Route path="/onboarding/setup" element={<OnboardingCompatRedirect fixedMode="business" step="setup" />} />
        <Route path="/onboarding/personal-setup" element={<OnboardingCompatRedirect fixedMode="personal" step="personal-setup" />} />
        <Route path="/onboarding/purpose" element={<OnboardingCompatRedirect fixedMode="business" step="setup" />} />
        <Route path="/onboarding/website" element={<OnboardingCompatRedirect fixedMode="business" step="setup" />} />
        <Route path="/onboarding/review" element={<OnboardingCompatRedirect fixedMode="business" step="review" />} />
        <Route path="/onboarding/invites" element={<OnboardingCompatRedirect fixedMode="business" step="invites" />} />
        <Route path="/onboarding/outcomes" element={<OnboardingCompatRedirect step="outcomes" />} />
        <Route path="/onboarding/pricing" element={<OnboardingCompatRedirect step="pricing" />} />
        <Route path="/onboarding/complete" element={<OnboardingCompatRedirect step="complete" />} />
        <Route path="/onboarding/type" element={<OnboardingCompatRedirect step="setup" />} />

        {/* Workspace Settings (workspace-scoped routes) */}
        <Route path="/workspace/:workspaceId/settings" element={<WorkspaceSettingsPage />} />
        <Route path="/workspace/:workspaceId/settings/:tab" element={<WorkspaceSettingsPage />} />

        {/* Settings routes — all handled by WorkspaceSettingsPage with session bridge */}
        <Route path="/settings" element={<WorkspaceSettingsPage />} />
        <Route path="/settings/:tab" element={<WorkspaceSettingsPage />} />
        <Route path="/settings/:tab/:action" element={<WorkspaceSettingsPage />} />

        {/* Admin Login */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
          }
        />
        <Route path="/*" element={<ProtectedRoutes />} />
      </Routes>
    </BrowserRouter>
  );
}
